import {Payrails} from '@payrails/web-sdk'
import {logToDataDog} from 'services/datadogLogger'
import {getCardFieldFonts} from 'utils/cardFieldFonts'

import type {
  ElementType,
  FramesContainer,
  InitOptions,
  PayrailsSecureField
} from '@payrails/web-sdk'

type CollectElementOptions = Parameters<
  FramesContainer['createCollectElement']
>[0]

interface SetupOptions {
  initResponse: InitOptions
  /** Must return a fresh init response, otherwise the session stays expired. */
  onSessionExpired: () => Promise<InitOptions | undefined>
}

let payrailsClient: Payrails | undefined
let collectContainer: FramesContainer | undefined
const collectElements = new Map<ElementType, PayrailsSecureField>()

/** Resolves to whether the SDK is ready. */
const setup = async ({
  initResponse,
  onSessionExpired
}: SetupOptions): Promise<boolean> => {
  // Already initialized - do nothing
  if (payrailsClient) return true

  try {
    // Loads the SDK runtime from the Payrails CDN.
    payrailsClient = await Payrails.init(initResponse)
    // The SDK re-inits the same instance from the returned response and
    // refreshes mounted elements, so Payrails.init must not be called again.
    payrailsClient.on('sessionExpired', onSessionExpired)
    return true
  } catch (error) {
    logToDataDog({
      logName: 'Services.PayRails.Init.Error',
      metadata: {
        errorMessage: error instanceof Error ? error.message : String(error)
      },
      level: 'error'
    })
    return false
  }
}

const getPayrailsInstance = () => payrailsClient

const getCollectContainer = () => {
  if (!collectContainer && payrailsClient) {
    collectContainer = payrailsClient.collectContainer({
      fonts: getCardFieldFonts()
    })
  }
  return collectContainer
}

const getCollectElement = (options: CollectElementOptions) => {
  if (!collectElements.has(options.type)) {
    const element = getCollectContainer()?.createCollectElement(options)
    if (element) collectElements.set(options.type, element)
  }
  return collectElements.get(options.type)
}

export const payrails = {
  setup,
  getPayrailsInstance,
  getCollectContainer,
  getCollectElement
}
